import React, {
  useCallback, useContext, useMemo, useState,
} from 'react'

import { func } from 'prop-types'

import GalleryFilters from 'Pods/Shared/GalleryFilters/GalleryFilters'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import { NOOP } from 'Utils/stableDefaults'

import EmptyState from './Components/EmptyState/EmptyState'
import Gallery from './Components/Gallery/Gallery'
import { DEFAULT_IMAGE_GALLERY_FILTERS } from './constants'
import { MOCK_IMAGE_CATEGORIES } from './Utils/mockImages'

// Community image gallery for /ai-image-generator/images. Filter state is local:
// unlike the 3D gallery nothing else in the studio reads it yet. Opening an image is
// handled inside the gallery (the expanded-view modal); the other card actions are
// wired by the host and are no-ops until their flows ship.
function ImageGalleryPanel({
  onImageEdit = NOOP,
  onImageDownload = NOOP,
  onImageGenerate3d = NOOP,
}) {
  const t = useTranslation()
  const { extractingCoreStyles } = useContext(ThreeDStudioPageContext)
  const categories = useMemo(
    () => MOCK_IMAGE_CATEGORIES.map(({ nameKey, value }) => ({ name: t(nameKey), value })),
    [t],
  )
  const [searchQuery, setSearchQuery] = useState(DEFAULT_IMAGE_GALLERY_FILTERS.searchQuery)
  const [category, setCategory] = useState(DEFAULT_IMAGE_GALLERY_FILTERS.category)

  const hasActiveFilters = searchQuery !== DEFAULT_IMAGE_GALLERY_FILTERS.searchQuery
    || category !== DEFAULT_IMAGE_GALLERY_FILTERS.category

  const handleClearFilters = useCallback(() => {
    setSearchQuery(DEFAULT_IMAGE_GALLERY_FILTERS.searchQuery)
    setCategory(DEFAULT_IMAGE_GALLERY_FILTERS.category)
  }, [])

  return (
    <div className="image-gallery-panel">
      <GalleryFilters
        className="image-gallery-panel__filters"
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('imageGallery.searchPlaceholder')}
        category={category}
        categories={categories}
        onCategoryChange={setCategory}
        morePlaceholder={t('imageGallery.moreCategories')}
      />
      <Gallery
        searchQuery={searchQuery}
        category={category}
        extractingCoreStyles={extractingCoreStyles}
        onImageEdit={onImageEdit}
        onImageDownload={onImageDownload}
        onImageGenerate3d={onImageGenerate3d}
        emptyState={(
          <EmptyState hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
        )}
      />
    </div>
  )
}

ImageGalleryPanel.propTypes = {
  onImageEdit: func,
  onImageDownload: func,
  onImageGenerate3d: func,
}

export default ImageGalleryPanel
